/**
 * Department Actions and Upcoming Operations for the new console (Traci's review, 23 Sep 2026; v109 §9, §10, §11).
 * Pure: rows in, cards out.
 *
 * "Every department has its flags and its own action cards. On the admin dashboard a department dropdown shows each
 * department's cards with their counts, so I can see how heavy their day is without opening their view." Each card
 * here is computed by the SAME rule the department's own queue uses (§2: counts and queues never disagree):
 *   Billing   the Billing workbench queues (src/lib/billing/queues.js) and the current dashboard's pay-talent rule
 *   Talent    Nautica's Action Center items, as the coordination API computes them (passed in, not recomputed)
 *   Editing   the Editing board (src/lib/editing/board.js)
 *   Sales     flags + logistics questionnaires locking soon / locked unsubmitted; GHL entry has no record to count yet
 *   Admin     insurance (read-only: purchase needed / awaiting the talent's form)
 *
 * Upcoming Operations (admin only): every event in the next 7 days, "Ready" or the outstanding actions from all
 * departments for that booking. Once the event date passes it drops off.
 */
import { classifyBillingQueues } from '../billing/queues.js'
import { QUEUE_ORDER as EDITING_QUEUES, QUEUE_TITLES as EDITING_TITLES } from '../editing/board.js'
import { dismissalKey, ghlEntryRows } from './actionCards.js'
import { missingInsuranceRows } from './insurance.js'

export const DEPARTMENTS = [
  { key: 'admin', label: 'Admin' },
  { key: 'sales', label: 'Sales' },
  { key: 'billing', label: 'Billing' },
  { key: 'talent_coordinator', label: 'Talent' },
  { key: 'editing', label: 'Editing' }
]

/**
 * Logistics questionnaire (Traci, 24 Sep 2026): it locks 7 days before the wedding, and one still not submitted at the
 * lock is overdue. Logistics is the only questionnaire we must have; the style questionnaire gets no warnings. Sales
 * sees it coming for the 30 days before the lock (her reminders go out 1 month, 1 week and 1 day before the lock).
 */
export const LOGISTICS_OVERDUE_DAYS = 7
export const LOGISTICS_DUE_SOON_DAYS = 30

/** The lock date for an event: event date − 7 days (YYYY-MM-DD). */
export function logisticsLockDate(eventDate) {
  const d = dateOnly(eventDate)
  return d ? addDays(d, -LOGISTICS_OVERDUE_DAYS) : null
}
export const UPCOMING_DAYS = 7

/**
 * Billing pays talent 30 days after that talent's completion form (JCF) came in (Traci, 28 Sep 2026: "payment due 30
 * days from upload of JCF"; it was the media upload + 27). Without a form on record, the media upload starts the clock.
 */
export const PAY_BY_DAYS = 30
/** The Love & Photos placeholder profiles (a video-only booking holds one as its photographer): never paid. */
const PLACEHOLDER_TALENT = new Set(['410935f9-6034-40b3-a65f-e9863de60e58', '7c7191dc-c5e5-43ed-b772-083463a00aa4'])
const ROLE_LABELS = { photographer: 'Photographer', videographer: 'Videographer', second_photographer: '2nd Photographer', second_videographer: '2nd Videographer' }

/** The date that starts the talent's payment clock, for one role: their JCF, else the media upload. */
export function uploadDateFor(b, role, forms = []) {
  const jcf = forms.find(f => f.booking_id === b?.id && f.talent_role === role && f.submitted_at)
  if (jcf) return businessDay(jcf.submitted_at)
  const own = role === 'videographer' || role === 'second_videographer' ? b?.video_uploaded_at : b?.photo_uploaded_at
  return businessDay(own || b?.media_uploaded_at) || null
}

/** Pay By for a talent payment: JCF (or upload) + 30 days; null until either is in. */
export function payByDate(b, role, forms = []) {
  const up = uploadDateFor(b, role, forms)
  return up ? addDays(up, PAY_BY_DAYS) : null
}

/**
 * Nautica's cards in the order they need to happen (Traci, 23 Sep 2026): coverage, client contact, 5-day check-in,
 * day-of check-in, Box folder, overdue media/form last.
 */
export const TALENT_CARD_ORDER = ['urgent_coverage', 'client_contact', 'five_day_checkin', 'day_of_checkin', 'box_folder', 'insurance_form', 'media_overdue']
const TALENT_TITLES = {
  urgent_coverage: 'Urgent Coverage Needed',
  client_contact: 'Client Contact Needed',
  five_day_checkin: '5-Day Check-In Needed',
  day_of_checkin: 'Day-Of Check-In Due',
  box_folder: 'Box Folder Needed',
  insurance_form: 'Insurance Form Needed',
  media_overdue: 'OVERDUE Media/Form'
}

const dayMs = 86400000
const dateOnly = v => (v ? String(v).slice(0, 10) : '')
/**
 * The business-day (Pacific) date of a timestamp. A JCF sent at 5:25 PM on Oct 6 in California is stored as
 * 2026-10-07T00:25Z, so slicing the UTC string put Pay By a day late (Traci, 4 Nov 2026: Kevin Bradbury).
 * Plain YYYY-MM-DD values pass through unchanged.
 */
const PACIFIC_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' })
const businessDay = v => {
  if (!v) return ''
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const t = Date.parse(s)
  return Number.isNaN(t) ? s.slice(0, 10) : PACIFIC_DAY.format(t)
}
const noon = iso => Date.parse(iso + 'T12:00:00Z')
const addDays = (iso, n) => new Date(noon(iso) + n * dayMs).toISOString().slice(0, 10)
const lower = v => String(v || '').toLowerCase()
const OPEN_FLAG = new Set(['open', 'escalated'])
const SUBMITTED = new Set(['submitted', 'completed', 'locked'])
const PAYMENT_READY_REVIEW = new Set(['approved', 'ready_for_delivery', 'delivered'])
const PAY_READY_STAGES = new Set(['media_approved', 'talent_paid', 'media_delivered_partial', 'media_delivered_full', 'booking_complete'])

const daysBetween = (from, to) => Math.round((noon(to) - noon(from)) / dayMs)
const card = (key, title, rows, extra = {}) => ({ key, title, count: rows ? rows.length : null, rows: rows || [], status: rows ? 'live' : 'not_tracked', ...extra })

/** Upcoming, not cancelled, real (no test rows, no 2099 placeholders). */
// Traci's doc: 10 days or less (was 7)
export const INSURANCE_CARD_DAYS = 10

export function isUpcoming(b, today, days) {
  const d = dateOnly(b?.event_date)
  if (!d || d < today || d >= '2099-01-01' || b?.is_test) return false
  if (lower(b?.booking_status).startsWith('cancel')) return false
  return days === null || days === undefined ? true : d <= addDays(today, days)
}

export function logisticsSubmitted(bookingId, questionnaires) {
  return SUBMITTED.has(lower(questionnaires.get(bookingId)?.status))
}

/**
 * The current Billing dashboard's "Media Approved - Pay Talent" rule, per talent slot. withoutMedia (booking_id:role):
 * talent the media job went ahead without, whose media never came in, are not paid (Traci, 29 Oct 2026).
 */
export function talentPaymentsDue(bookings, withoutMedia = new Set()) {
  const out = []
  const ready = (b, media) => b.review_status === 'approved' || b.media_approved === true
    || PAYMENT_READY_REVIEW.has(media === 'video' ? b.video_review_status : b.photo_review_status)
    || PAY_READY_STAGES.has(b.lifecycle_stage)
  for (const b of bookings) {
    if (lower(b.booking_status).startsWith('cancel') || b.is_test) continue
    // A placeholder in a talent slot is nobody to pay (Kendra, 28 Sep 2026: a video-only booking showed "Love & Photos ·
    // Photographer" on Pay Talent and could not be cleared)
    const real = id => id && !PLACEHOLDER_TALENT.has(id)
    if (real(b.photographer_id) && !b.talent_paid && ready(b, 'photo')) out.push({ booking: b, role: 'photographer', talent_id: b.photographer_id })
    if (real(b.videographer_id) && !b.videographer_paid && ready(b, 'video')) out.push({ booking: b, role: 'videographer', talent_id: b.videographer_id })
    if (real(b.second_photographer_id) && !b.second_photographer_paid && ready(b, 'photo')) out.push({ booking: b, role: 'second_photographer', talent_id: b.second_photographer_id })
    if (real(b.second_videographer_id) && !b.second_videographer_paid && ready(b, 'video')) out.push({ booking: b, role: 'second_videographer', talent_id: b.second_videographer_id })
  }
  return out.filter(x => !withoutMedia.has(`${x.booking.id}:${x.role}`))
}

/** Insurance, read-only: which upcoming bookings still need a COI bought, or are waiting on the talent's form. */
export function insuranceStatus(forms) {
  const out = new Map()
  for (const f of forms || []) {
    if (f.coi_purchased || lower(f.admin_action_status) === 'purchased' || lower(f.admin_action_status) === 'resolved') continue
    const state = lower(f.form_status) === 'completed' ? 'ready_to_purchase' : 'awaiting_talent_form'
    const prev = out.get(f.booking_id)
    if (!prev || state === 'ready_to_purchase') out.set(f.booking_id, state)
  }
  return out
}

/**
 * @param {object} p
 * @param {string}   p.today
 * @param {object[]} p.bookings        bookings rows (see route for the field list)
 * @param {object[]} p.balances        booking_balances rows
 * @param {Map}      p.questionnaires  booking_id -> { status }
 * @param {object[]} p.flags           flagged_accounts rows
 * @param {object[]} p.insurance       insurance_forms rows
 * @param {object[]|null} p.talentItems  coordination Action Center items ({ kind, booking_id, role, event_date, ... }), null when unavailable
 * @param {object|null}   p.editing     buildEditingBoard() result, null when not loaded
 * @param {object[]} p.qboTalent       talent needing QuickBooks onboarding ({ id, name, first_booking_id, first_booking_date })
 * @param {Map}      p.names           id -> display name (customers by customer_id, talent by photographer id)
 * @param {object|null} p.cancellations buildCancellationRows() result ({ refund24h, cancellations }), null when not loaded
 * @param {Map|null} p.contacts        customer id -> { email, phone } for the GHL card; null when not loaded
 * @param {object[]|null} p.applications Waiting Room rows (waitingRoomRows); null until its migration has run
 * @param {Set}      p.withoutMedia    booking_id:role the media job went ahead without (board.js withoutMediaKeys): not paid
 * @param {object[]} p.forms           job_completion_forms rows (booking_id, talent_role, submitted_at): start Pay By
 */
export function buildDepartmentActions({ today, bookings = [], balances = [], questionnaires = new Map(), flags = [], insurance = [], talentItems = null, editing = null, qboTalent = [], names = new Map(), cancellations = null, contacts = null, applications = null, withoutMedia = new Set(), forms = [] }) {
  const byId = new Map(bookings.map(b => [b.id, b]))
  const row = (bookingId, detail, extra = {}) => {
    const b = byId.get(bookingId) || {}
    return { booking_id: bookingId, client_id: b.customer_id || null, client_name: names.get(b.customer_id) || null, event_date: dateOnly(b.event_date) || null, detail, ...extra }
  }
  const flagsFor = role => flags.filter(f => OPEN_FLAG.has(lower(f.status)) && f.assigned_to_role === role)
    .map(f => ({ flag_id: f.id, client_id: f.user_id, client_name: names.get(f.user_id) || null, detail: f.reason || 'Flag', severity: f.severity || null, created_at: f.created_at }))

  // Sales
  const logisticsOpen = bookings
    .filter(b => isUpcoming(b, today, LOGISTICS_OVERDUE_DAYS + LOGISTICS_DUE_SOON_DAYS) && !logisticsSubmitted(b.id, questionnaires) && lower(b.package_type) !== 'engagement')
    .sort((a, b) => dateOnly(a.event_date).localeCompare(dateOnly(b.event_date)))
  const started = b => (questionnaires.has(b.id) ? `Questionnaire ${lower(questionnaires.get(b.id).status).replace(/_/g, ' ') || 'started'}` : 'Questionnaire not started')
  const overdueLogistics = logisticsOpen
    .filter(b => logisticsLockDate(b.event_date) <= today)
    .map(b => row(b.id, `${started(b)} · locked ${logisticsLockDate(b.event_date)}`))
  const logisticsDueSoon = logisticsOpen
    .filter(b => logisticsLockDate(b.event_date) > today)
    .map(b => {
      const lock = logisticsLockDate(b.event_date)
      const left = daysBetween(today, lock)
      return row(b.id, `${started(b)} · locks ${lock} (${left} day${left === 1 ? '' : 's'})`, { lock_date: lock, days_to_lock: left, item_key: `${b.id}:${lock}` })
    })

  // Billing
  const active = balances.filter(r => r.is_active !== false && !r.is_placeholder)
  const { byQueue } = classifyBillingQueues(active, today)
  const money = r => {
    const due = dateOnly(r.final_due_date) || null
    return row(r.booking_id, `$${Math.round(Number(r.remaining_cents || 0) / 100).toLocaleString('en-US')} open${due ? ` · due ${due}` : ''}`, { item_key: `${r.booking_id}:${due || 'no-due-date'}`, remaining_cents: Number(r.remaining_cents || 0), due_date: due, days_overdue: due && due < today ? daysBetween(due, today) : 0, plan: r.payment_plan || null })
  }
  const payTalent = talentPaymentsDue(bookings, withoutMedia).map(x => row(x.booking.id, `${names.get(x.talent_id) || 'Talent'} · ${ROLE_LABELS[x.role]}`, { talent_id: x.talent_id, talent_name: names.get(x.talent_id) || null, role: x.role, role_label: ROLE_LABELS[x.role], hours: Number(x.booking.hours_booked) || null, uploaded_at: uploadDateFor(x.booking, x.role, forms), pay_by: payByDate(x.booking, x.role, forms), item_key: `${x.booking.id}:${x.role}` }))
    // Earliest Pay By first, overdue included (Kendra via Traci, 22 Oct 2026: it read in talent-name order)
    .sort((a, b) => String(a.pay_by || '9999').localeCompare(String(b.pay_by || '9999')) || String(a.event_date || '').localeCompare(String(b.event_date || '')) || String(a.talent_name || '').localeCompare(String(b.talent_name || '')))
  const cancelRow = r => ({ booking_id: r.booking_id, client_id: r.client_id, client_name: r.client_name, event_date: r.event_date, detail: r.detail, action: r.action || null, cancelled_at: r.cancelled_at })

  // Admin. Traci's doc (Oct 2026): the card counts bookings that still need insurance bought 7 days out or less; the
  // Insurance page keeps the full list.
  const insuranceByBooking = insuranceStatus(insurance)
  // Insured bookings whose talent has not opened the form have no form row yet (Traci: the card showed 0 while the
  // Insurance page showed 5); they are waiting on the talent's form too.
  for (const m of missingInsuranceRows(bookings, insurance)) if (!insuranceByBooking.has(m.booking.id)) insuranceByBooking.set(m.booking.id, 'awaiting_talent_form')
  const insuranceRows = [...insuranceByBooking.entries()]
    .filter(([id]) => isUpcoming(byId.get(id), today, INSURANCE_CARD_DAYS))
    .map(([id, state]) => row(id, state === 'ready_to_purchase' ? 'Ready to purchase' : 'Awaiting talent form', { state }))
    .sort((a, b) => String(a.event_date).localeCompare(String(b.event_date)))

  // Talent coordinator
  const talentCards = talentItems
    ? TALENT_CARD_ORDER.map(k => card(k, TALENT_TITLES[k], talentItems.filter(i => i.kind === k).map(i => ({ booking_id: i.booking_id, client_id: i.customer_id || null, client_name: i.customer_name || names.get(byId.get(i.booking_id)?.customer_id) || null, event_date: dateOnly(i.event_date) || null, detail: i.what || i.detail || null, role: i.role || null, item_key: `${i.booking_id}:${i.role || ''}` }))))
    : TALENT_CARD_ORDER.map(k => card(k, TALENT_TITLES[k], null, { note: 'Action Center did not load' }))

  const editingCards = editing
    ? EDITING_QUEUES.map(k => card(k, EDITING_TITLES[k], editing.queues[k].map(r => ({ booking_id: r.booking_id, client_id: r.client_id, client_name: r.client_name, event_date: r.event_date, detail: [r.talent_name, r.media_type, r.deadline ? `due ${r.deadline}` : null].filter(Boolean).join(' · '), risk: r.risk, item_key: r.key }))))
    : EDITING_QUEUES.map(k => card(k, EDITING_TITLES[k], null, { note: 'Editing board did not load' }))

  return {
    admin: [
      card('insurance', 'Insurance — Purchase Needed', insuranceRows, { link: 'insurance' }),
      card('admin_flags', 'Admin Flags', flagsFor('admin'), { link: 'notifications' })
    ],
    sales: [
      card('sales_flags', 'Sales Flags', flagsFor('sales'), { link: 'notifications' }),
      card('ghl_entry', 'New Bookings — Enter in GHL', contacts ? ghlEntryRows({ bookings, names, contacts }) : null, contacts ? { note: 'new bookings until marked entered in GHL' } : { note: 'Contacts did not load' }),
      card('logistics_due_soon', 'Logistics Questionnaire — Locking Soon', logisticsDueSoon, { note: `locks within ${LOGISTICS_DUE_SOON_DAYS} days, not submitted yet` }),
      card('overdue_logistics', 'Upcoming Events — Overdue Logistics Questionnaire', overdueLogistics, { note: `locked ${LOGISTICS_OVERDUE_DAYS} days before the event without a submission` })
    ],
    billing: [
      card('billing_flags', 'Billing Flags', flagsFor('billing'), { link: 'notifications' }),
      card('pay_talent', 'Media Approved — Pay Talent', payTalent, { link: 'billing' }),
      card('qbo_invite', 'Send QBO Onboarding Invite', qboTalent.map(t => ({ talent_id: t.id, talent_name: t.name || null, client_name: t.name || null, booking_id: t.first_booking_id || null, event_date: dateOnly(t.first_booking_date) || null, first_client: names.get(byId.get(t.first_booking_id)?.customer_id) || t.first_client || null, email: t.email || null, qbo_status: t.status || 'not_sent', invited_at: dateOnly(t.invited_at) || null, detail: t.status ? `QBO ${t.status.replace(/_/g, ' ')}` : 'QBO not sent', item_key: `talent:${t.id}` }))),
      card('overdue_follow_up', 'Overdue Requiring Follow-Up', (byQueue.overdue || []).map(money), { link: 'billing' }),
      card('invoicing_due_7', 'Invoicing — Due in 7 Days', (byQueue.due_soon || []).map(money), { link: 'billing' }),
      card('refund_24h', '24-Hour Cancellation — Refund', cancellations ? cancellations.refund24h.map(cancelRow) : null, cancellations ? { note: 'cancelled within 24 hours of booking with money paid; clears when the refund is recorded' } : { note: 'Cancellations did not load' }),
      card('client_cancellations', 'Client Cancellations', cancellations ? cancellations.cancellations.map(cancelRow) : null, cancellations ? { note: 'cancelled bookings for Billing to review; clear each one when handled' } : { note: 'Cancellations did not load' })
    ],
    talent_coordinator: [
      ...talentCards,
      card('talent_flags', 'Talent Flags', flagsFor('talent_coordinator'), { link: 'notifications' }),
      card('applications_pending', 'Applications Pending', applications ? applications.map(a => ({ client_name: a.name, detail: [a.role === 'videographer' ? 'Videographer' : 'Photographer', a.experience_years !== null && a.experience_years !== '' ? `${a.experience_years} yrs` : null, a.submitted_at ? `applied ${dateOnly(a.submitted_at)}` : null].filter(Boolean).join(' · '), item_key: `application:${a.id}` })) : null, applications ? { link: 'waiting_room', note: 'awaiting portfolio review in the Waiting Room' } : { note: 'Needs the Waiting Room migration' })
    ],
    editing: [
      card('editing_flags', 'Editing Flags', flagsFor('editing'), { link: 'notifications' }),
      ...editingCards
    ]
  }
}

/** Upcoming Operations: events in the next 7 days, each "Ready" or its outstanding actions across departments. */
export function buildUpcomingOperations({ today, bookings = [], balances = [], questionnaires = new Map(), insurance = [], talentItems = [], names = new Map() }) {
  const balById = new Map(balances.map(r => [r.booking_id, r]))
  const insuranceByBooking = insuranceStatus(insurance)
  const itemsBy = new Map()
  for (const i of talentItems || []) {
    if (!itemsBy.has(i.booking_id)) itemsBy.set(i.booking_id, [])
    itemsBy.get(i.booking_id).push(i)
  }
  return bookings
    .filter(b => isUpcoming(b, today, UPCOMING_DAYS))
    .sort((a, b) => dateOnly(a.event_date).localeCompare(dateOnly(b.event_date)))
    .map(b => {
      const actions = []
      for (const i of itemsBy.get(b.id) || []) actions.push({ department: 'talent_coordinator', label: TALENT_TITLES[i.kind] || i.kind, detail: i.what || null })
      if (!logisticsSubmitted(b.id, questionnaires) && lower(b.package_type) !== 'engagement') actions.push({ department: 'sales', label: 'Logistics questionnaire not submitted' })
      const bal = balById.get(b.id)
      if (bal && !bal.needs_backfill && Number(bal.remaining_cents || 0) > 0) actions.push({ department: 'billing', label: `Balance open: $${Math.round(Number(bal.remaining_cents) / 100).toLocaleString('en-US')}` })
      const ins = insuranceByBooking.get(b.id)
      if (ins) actions.push({ department: 'admin', label: ins === 'ready_to_purchase' ? 'Purchase insurance' : 'Insurance: awaiting talent form' })
      return {
        booking_id: b.id,
        client_id: b.customer_id || null,
        client_name: names.get(b.customer_id) || null,
        event_date: dateOnly(b.event_date),
        location: [b.location_city, b.location_state].filter(Boolean).join(', ') || null,
        package_type: b.package_type || null,
        ready: actions.length === 0,
        actions
      }
    })
}

/**
 * Fold Nautica's Action Center items (from /api/admin/coordination/actions, the endpoint her workbench uses) into a
 * dashboard result computed without them: the Talent department's cards, and each upcoming event's actions.
 */
export function withTalentItems({ departments, upcoming }, items, dismissed = []) {
  if (!Array.isArray(items)) return { departments, upcoming }
  const gone = new Set(dismissed || [])
  const cards = TALENT_CARD_ORDER.map(k => card(k, TALENT_TITLES[k], items.filter(i => i.kind === k).map(i => ({
    booking_id: i.booking_id, client_id: i.customer_id || null, client_name: i.customer_name || null, event_date: dateOnly(i.event_date) || null, detail: i.what || null, role: i.role || null, item_key: `${i.booking_id}:${i.role || ''}`
  })).filter(r => !gone.has(dismissalKey(k, r.item_key))), { clearable: true }))
  const rest = (departments.talent_coordinator || []).filter(c => !TALENT_CARD_ORDER.includes(c.key))
  const by = new Map()
  for (const i of items) { if (!by.has(i.booking_id)) by.set(i.booking_id, []); by.get(i.booking_id).push(i) }
  const merged = (upcoming || []).map(u => {
    const extra = (by.get(u.booking_id) || []).map(i => ({ department: 'talent_coordinator', label: TALENT_TITLES[i.kind] || i.kind, detail: i.what || null }))
    const actions = [...extra, ...u.actions.filter(a => a.department !== 'talent_coordinator')]
    return { ...u, actions, ready: actions.length === 0 }
  })
  return { departments: { ...departments, talent_coordinator: [...cards, ...rest] }, upcoming: merged }
}
